import type { ServiceProvider, Vendor } from '@shopgnd/shared';
import { resolveEntryDirectory } from '@/lib/businessDirectoryTaxonomy';

/** Customer-facing name for a service pro profile. */
export function proPublicName(
  pro: Pick<ServiceProvider, 'providerType' | 'companyName' | 'headline'>,
): string {
  return pro.providerType === 'company' && pro.companyName ? pro.companyName : pro.headline;
}

/** Public business slug for a standalone service pro (no vendor shell). */
export function proBusinessSlug(pro: Pick<ServiceProvider, 'id'>): string {
  return `pro-${pro.id.slice(0, 8)}`;
}

export function parseProBusinessSlug(param: string): string | null {
  const trimmed = param.trim().toLowerCase();
  if (!trimmed.startsWith('pro-')) return null;
  const prefix = trimmed.slice(4);
  return prefix.length >= 8 ? prefix : null;
}

/** Synthetic vendor row for hire-only pros until linked to a real Vendor record. */
export function buildProVendorShell(pro: ServiceProvider): Vendor {
  const { sector, categoryId } = resolveEntryDirectory(
    {
      id: `hire-${pro.id}`,
      businessType: 'trades',
      primaryMode: 'hire',
    } as Vendor,
    pro,
  );
  return {
    id: `hire-${pro.id}`,
    ownerUserId: pro.ownerUserId,
    businessName: proPublicName(pro),
    slug: proBusinessSlug(pro),
    description: pro.bio,
    logoUrl: pro.logoUrl ?? pro.avatarUrl,
    phone: pro.phone,
    website: pro.website,
    instagramUrl: pro.instagramUrl,
    facebookUrl: pro.facebookUrl,
    tiktokUrl: pro.tiktokUrl,
    linkedinUrl: pro.linkedinUrl,
    youtubeUrl: pro.youtubeUrl,
    xUrl: pro.xUrl,
    ratingAvg: pro.ratingAvg,
    ratingCount: pro.ratingCount,
    payoutsEnabled: false,
    acceptsCardPayments: false,
    offersPickup: false,
    offersDelivery: false,
    pickupParish: pro.parish,
    approvalStatus: 'approved',
    businessType: 'trades',
    directorySector: sector,
    directoryCategoryId: categoryId,
    businessModes: ['hire'],
    primaryMode: 'hire',
    linkedProId: pro.id,
    createdAt: pro.createdAt,
  };
}
